import { Store } from '@ngrx/store';
import { Observable, of, take, map, filter, switchMap } from 'rxjs';

import { RelatedProduct, RelatedProducts, RelatedsCarousels } from '../../domain/models/related-products.models';

import { Category } from '@core/domain/models/category.models';
import { getCmsConfigValue } from '@core/infrastructure/state/core.selectors';
import { getCategory } from '@pages/category';
import { CrossSelling } from '@shared/ab-testing/models/AB-testing.models';
import { ABTestingService } from '@shared/ab-testing/services/AB-testing.service';
import { ProductViewPlace } from '@shared/product/models';

// #region PRIVATE
const getCrossSellingABTest = (abTestingService: ABTestingService): CrossSelling => {
    return abTestingService.getCrossSelling();
};

const getCmsRelatedProductFromVariable = (store: Store, crossSelling: CrossSelling) => {
    return store.select(getCategory).pipe(
        filter((category: Category) => category !== null && category !== undefined),
        switchMap((category: Category) => {
            let cmsVariable = 'related_products';
            if (!crossSelling.categoryIds?.some((id: string) => id === category.id)) {
                cmsVariable = crossSelling.cmsVariable;
            }
            return store.select(getCmsConfigValue(cmsVariable));
        }),
        take(1)
    );
};

const getCmsRelatedProducts = (
    abTestingService: ABTestingService,
    store: Store
): Observable<RelatedProducts[]> => {
    // ECOMDUTI-12806 TESTAB TEST A/B - Optimize - Grupo Venta Cruzada
    // ECOMDUTI-14079 TESTAB TEST A/B - Venta Cruzada - Excepcionar productos que están categorizados en X parrilla
    const crossSelling: CrossSelling = getCrossSellingABTest(abTestingService);
    let cmsVariable = 'related_products';

    if (crossSelling?.cmsVariable && crossSelling.categoryIds?.length) {
        return getCmsRelatedProductFromVariable(store, crossSelling);
    }
    if (crossSelling && crossSelling?.cmsVariable !== '') {
        cmsVariable = crossSelling.cmsVariable;
    }
    const selector = getCmsConfigValue(cmsVariable);
    return store.select(selector);
};

/**
 * Due to compatibility issues between the new algorithm completeLookYoda and the old algorithm's analytics, we need to
 * transform the type of the items in the carousel in a very unsteady way.
 *
 * Refactor to move this transformation to the infrastructure layer could help to understand the purpose better.
 *
 * @param item the carousel item to transform
 * @returns the transformed item
 */
function carouselItemTransformer(item) {
    const algorithmNamingCompatibilityMap = {
        completeLookYoda: 'completatulook_yoda',
    };

    return {
        ...item,
        type: algorithmNamingCompatibilityMap[item.type] || item.type,
    };
}
// #endregion PRIVATE

// #region PUBLIC
export const getRelatedsCarouselsToShowUseCase = (
    abTestingService: ABTestingService,
    store: Store,
    place: ProductViewPlace
): Observable<RelatedsCarousels> => {
    if (!place) {
        return of({
            relatedProduct: [],
            imageType: undefined,
        });
    }

    // TO REFACTOR: Mover a la capa de infraestructura
    const relatedProducts$: Observable<RelatedProducts[]> = getCmsRelatedProducts(abTestingService, store);

    return relatedProducts$.pipe(
        take(1),
        map((relatedProducts: RelatedProducts[]) => {
            const relatedPlace = [...relatedProducts]?.find((related: RelatedProducts) => related.place === place);

            if (!relatedPlace) {
                return {
                    relatedProduct: [],
                    imageType: undefined,
                };
            }

            return {
                relatedProduct: relatedPlace.related_products
                    .filter((carousel: RelatedProduct) => carousel.visible).map((carousel) => {
                        return {
                            ...carousel,
                            items: carousel.items?.map((item) => carouselItemTransformer(item)) || [],
                        };
                    }),
                imageType: relatedPlace.imageType,
            };
        })
    );
};
// #endregion PUBLIC

// #region __TESTS__
export const __tests__ = {
    getCrossSellingABTest,
    getCmsRelatedProductFromVariable,
    getCmsRelatedProducts,
};
// #endregion __TESTS__
